// Permissions utility for role-based access control

import { MasterRole, hasPermission as checkPermission, getRoleDefinition } from '../constants/masterRoles';

/**
 * Check if a user with a given role has a specific permission
 */
export const canAccess = (role: MasterRole | undefined, permission: keyof ReturnType<typeof getRoleDefinition>['permissions']): boolean => {
  if (!role) return false;
  return checkPermission(role, permission);
};

/**
 * Check if user can manage platform configuration
 */
export const canManagePlatformConfig = (role: MasterRole | undefined): boolean => {
  return canAccess(role, 'managePlatformConfig');
};

/**
 * Check if user can manage user roles
 */
export const canManageUserRoles = (role: MasterRole | undefined): boolean => {
  return canAccess(role, 'manageUserRoles');
};

/**
 * Check if user can create events
 */
export const canCreateEvents = (role: MasterRole | undefined): boolean => {
  return canAccess(role, 'createEvents');
};

/**
 * Check if user can edit events
 */
export const canEditEvents = (role: MasterRole | undefined): boolean => {
  return canAccess(role, 'editEvents');
};

/**
 * Check if user can delete events
 */
export const canDeleteEvents = (role: MasterRole | undefined): boolean => {
  return canAccess(role, 'deleteEvents');
};

/**
 * Check if user can view financial reports
 */
export const canViewFinancialReports = (role: MasterRole | undefined): boolean => {
  return canAccess(role, 'viewFinancialReports');
};

/**
 * Check if user can access database
 */
export const canAccessDatabase = (role: MasterRole | undefined): boolean => {
  return canAccess(role, 'databaseAccess');
};

/**
 * Check if user can issue tickets
 */
export const canIssueTickets = (role: MasterRole | undefined): boolean => {
  return canAccess(role, 'issueTickets');
};

/**
 * Check if user can manage client profiles
 */
export const canManageClientProfiles = (role: MasterRole | undefined): boolean => {
  return canAccess(role, 'manageClientProfiles');
};

/**
 * Check if user can view analytics
 */
export const canViewAnalytics = (role: MasterRole | undefined): boolean => {
  return canAccess(role, 'viewAnalytics');
};

/**
 * Get all permissions for a role
 */
export const getRolePermissions = (role: MasterRole | undefined): Record<string, boolean> => {
  if (!role) return {};
  const roleDef = getRoleDefinition(role);
  return roleDef.permissions as Record<string, boolean>;
};

/**
 * Get restrictions for a role
 */
export const getRoleRestrictions = (role: MasterRole | undefined): string[] => {
  if (!role) return [];
  const roleDef = getRoleDefinition(role);
  return roleDef.restrictions;
};

